// Builds + dispatches `com.ping.image` through MLS (encrypted blob on S3).

import type { EditedImageBundle, ImageSource } from '@ping/ui';
import {
  commitSentMessage as sharedCommitSentMessage,
  encodeEnvelopeJsonBytes,
  encodeEnvelopeJsonString,
  lastMessagePreview,
  markMessageFailed as sharedMarkMessageFailed,
  mintEventId,
  persistSentMessage as sharedPersistSentMessage,
} from '@ping/ui';

import { log } from '@core/log/log';
import { retrieveIdentity } from '@core/persistence/identity';

import { exportEditedImage } from './exportEditedImage';
import { blobRefToWire, type BlobRefWire,encryptMediaBlob } from './mediaCrypto';
import { uploadCiphertextMultipart } from './mediaUpload';
import { webConversationsPersister, webMessagesPersister } from './persistenceAdapters';

interface SentMessageEnvelope {
  conversation_id: Uint8Array;
  epoch: number | bigint;
  content_hash: Uint8Array;
  hlc: { wall_ms: number | bigint; logical: number };
}

interface SendableConversation {
  send(plaintext: Uint8Array): Promise<SentMessageEnvelope>;
}

export interface ImageEnvelopePayload {
  readonly blob_ref: BlobRefWire;
  readonly w: number;
  readonly h: number;
  readonly mime: string;
  readonly caption?: string | undefined;
}

export interface ImageEnvelope {
  readonly kind: 'com.ping.image';
  readonly payload: ImageEnvelopePayload;
  /** Thread root content-hash when sent as a reply into a thread. */
  readonly parent_id?: string;
}

export interface SendImageMessageOptions {
  readonly conv: SendableConversation;
  readonly conversationId: string;
  readonly bundle: EditedImageBundle;
  readonly source: ImageSource;
  readonly signal?: AbortSignal | undefined;
  /**
   * Precomputed local-echo row id. When supplied, the helper upserts
   * the SAME row the caller already wrote (pending → sent) instead of
   * inserting a fresh one — keeps the on-screen bubble stable so it
   * never re-mounts / reorders mid-upload. The caller (`WebChatRoom`)
   * also drives the uploading variant + live % off this id.
   */
  readonly localId?: string | undefined;
  /** Caption sent alongside the image (from the tray preview overlay). */
  readonly caption?: string | undefined;
  /** Live upload progress (0..1) — forwarded to `uploadCiphertextMultipart`. */
  readonly onProgress?: ((fraction: number) => void) | undefined;
  /** When set, posts the image as a reply into this thread root. */
  readonly parentId?: string | undefined;
}

export interface SendImageMessageResult {
  readonly contentHashHex: string;
  readonly messageId: string;
  readonly previewObjectUrl: string;
}

/** Identity bundle (no crop/draw) for direct file → upload. */
export function bundleForSource(source: ImageSource): EditedImageBundle {
  return {
    imageId: source.id,
    originalSize: { widthPx: source.widthPx, heightPx: source.heightPx },
    orientationQuarterTurns: 0,
    operations: [],
  };
}

export async function sendImageMessage(opts: SendImageMessageOptions): Promise<SendImageMessageResult> {
  const { conv, conversationId, bundle, source, signal, localId, caption, onProgress, parentId } = opts;

  const identity = await retrieveIdentity();

  const exported = await exportEditedImage(bundle, source);
  const { ciphertext, secrets, hash } = await encryptMediaBlob(exported.bytes);
  let mediaId: string;
  try {
    ({ mediaId } = await uploadCiphertextMultipart(
      ciphertext,
      exported.mime,
      signal,
      onProgress,
    ));
  } catch (err) {
    // Flip the pending bubble to 'failed' so the retry affordance
    // surfaces instead of leaving a perpetual "uploading" spinner.
    if (localId !== undefined) {
      await sharedMarkMessageFailed({ messages: webMessagesPersister, id: localId });
    }
    throw err;
  }

  const blobRef = blobRefToWire(mediaId, secrets, hash);
  const payload: ImageEnvelopePayload = {
    blob_ref: blobRef,
    w: exported.width,
    h: exported.height,
    mime: exported.mime,
    ...(caption !== undefined && caption.length > 0 ? { caption } : {}),
  };
  // Align wire event_id with the pending local-echo row when one exists
  // so a retry reuses the same plaintext identity (server send-idempotency).
  const eventId = localId ?? mintEventId();
  const encodable = {
    kind: 'com.ping.image' as const,
    event_id: eventId,
    // A thread reply must self-identify with `payload.thread: true` so the
    // main timeline folds it out (parent_id ALONE also marks an inline
    // reply-quote, which must stay inline). For non-text kinds a parent_id
    // only ever means a thread reply, so key the flag off it — mirroring the
    // text reply's `{ body, thread: true }`. See WebChatRoom's timeline fold.
    payload: { ...payload, ...(parentId !== undefined ? { thread: true } : {}) },
    ...(parentId !== undefined ? { parent_id: parentId } : {}),
  };
  const payloadBody = encodeEnvelopeJsonString(encodable);
  const payloadBytes = encodeEnvelopeJsonBytes(encodable);

  let sentEnvelope: SentMessageEnvelope;
  try {
    sentEnvelope = await conv.send(payloadBytes);
  } catch (err) {
    if (localId !== undefined) {
      await sharedMarkMessageFailed({ messages: webMessagesPersister, id: localId });
    }
    throw err;
  }
  const contentHashHex = bytesToHex(sentEnvelope.content_hash);

  const previewObjectUrl = URL.createObjectURL(
    new Blob([exported.bytes.buffer.slice(exported.bytes.byteOffset, exported.bytes.byteOffset + exported.bytes.byteLength) as ArrayBuffer], {
      type: exported.mime,
    }),
  );

  const sentAt = Date.now();
  const epoch =
    typeof sentEnvelope.epoch === 'bigint' ? Number(sentEnvelope.epoch) : sentEnvelope.epoch;

  // Local-echo commit. When the caller pre-wrote a pending row
  // (`localId`), patch the SAME row in place: first swap the
  // placeholder body for the real `blob_ref` envelope, then flip
  // pending → sent via the shared `commitSentMessage`. Otherwise fall
  // back to the legacy single-shot insert (no instant bubble).
  if (localId !== undefined) {
    await webMessagesPersister.update(localId, { body: payloadBody });
    if (identity) {
      const preview = lastMessagePreview(payloadBody);
      await sharedCommitSentMessage({
        messages: webMessagesPersister,
        conversations: webConversationsPersister,
        id: localId,
        conversationId,
        contentHash: contentHashHex,
        epoch,
        serverSeq: sentAt,
        sentAt,
        senderAccountId: identity.accountId,
        ...(preview !== undefined ? { preview } : {}),
      });
    }
    log.debug({
      event: 'image.send.ok',
      conversationIdPrefix: conversationId.slice(0, 6),
      mediaIdPrefix: mediaId.slice(0, 8),
      sizeBytes: exported.bytes.byteLength,
    });
    return { contentHashHex, messageId: localId, previewObjectUrl };
  }

  // Same id as wire event_id — keeps local + peer identity aligned.
  const messageId = eventId;

  if (identity) {
    await sharedPersistSentMessage({
      messages: webMessagesPersister,
      conversations: webConversationsPersister,
      record: {
        id: messageId,
        conversationId,
        epoch,
        serverSeq: sentAt,
        contentHash: contentHashHex,
        senderAccountId: identity.accountId,
        body: payloadBody,
        sentAt,
        deliveryState: 'sent',
        ...(parentId !== undefined
          ? { inReplyTo: { messageId: parentId, preview: 'Photo' } }
          : {}),
      },
    });
  } else {
    log.warn({ event: 'image.persistSent.no_identity' });
  }

  log.debug({
    event: 'image.send.ok',
    conversationIdPrefix: conversationId.slice(0, 6),
    mediaIdPrefix: mediaId.slice(0, 8),
    sizeBytes: exported.bytes.byteLength,
  });

  return { contentHashHex, messageId, previewObjectUrl };
}

export function isImageEnvelopeBody(body: string | undefined): boolean {
  if (body === undefined || body.length === 0) return false;
  if (!body.startsWith('{')) return false;
  return body.includes('"com.ping.image"');
}

export function parseImageEnvelope(body: string | undefined): ImageEnvelopePayload | undefined {
  if (body === undefined) return undefined;
  try {
    const parsed = JSON.parse(body) as Partial<ImageEnvelope>;
    if (parsed.kind !== 'com.ping.image') return undefined;
    const p = parsed.payload;
    const ref = p?.blob_ref;
    if (
      p === undefined ||
      ref === undefined ||
      typeof ref.media_id !== 'string' ||
      typeof ref.content_key !== 'string' ||
      typeof ref.content_iv !== 'string' ||
      typeof ref.hash !== 'string' ||
      typeof p.w !== 'number' ||
      typeof p.h !== 'number' ||
      typeof p.mime !== 'string'
    ) {
      return undefined;
    }
    return {
      blob_ref: ref,
      w: p.w,
      h: p.h,
      mime: p.mime,
      ...(typeof p.caption === 'string' ? { caption: p.caption } : {}),
    };
  } catch {
    return undefined;
  }
}

function bytesToHex(b: Uint8Array): string {
  let out = '';
  for (let i = 0; i < b.length; i++) {
    out += (b[i] ?? 0).toString(16).padStart(2, '0');
  }
  return out;
}
